import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { marketAbuseApi } from '@/api/services/marketAbuse';
import type { NotificationFilterInput, SettlementTimeAlertDto, VolumeAlertDto } from '@/api/types/marketAbuse';

const num = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));
const dmy = (iso: string) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? iso : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };
const dmyhms = (iso: string) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? iso : `${dmy(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`; };
function yStr() { const d = new Date(); d.setDate(d.getDate() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

type Applied = { mode: 'filtered' | 'all'; input: NotificationFilterInput } | null;
export interface JumpPayload { symbol: string; startDate: string }

export function NotificationsTab({ jump, onOpenLogin }: { jump: JumpPayload | null; onOpenLogin: (login: number, connector: number) => void }) {
  const toast = useToast();
  const y = yStr();
  const [draft, setDraft] = useState({ startDate: y, endDate: y, notifSymbol: null as string | null, notifExecutionVenue: null as number | null });
  const [applied, setApplied] = useState<Applied>(null);
  const venues = useQuery({ queryKey: ['rd-filter', 'venues'], queryFn: filterDataApi.getExecutionVenues, staleTime: 5 * 60_000 });

  const base = (): NotificationFilterInput => ({ startDate: draft.startDate, endDate: draft.endDate, notifSymbol: draft.notifSymbol || null, notifExecutionVenue: draft.notifExecutionVenue ?? null, skipCount: 0, maxResultCount: 1000 });

  const settlement = useQuery({
    queryKey: ['ma-settle', applied],
    queryFn: () => (applied!.mode === 'all' ? marketAbuseApi.getUnfilteredSettlementTimeNotifications(applied!.input) : marketAbuseApi.getSettlementTimeNotifications(applied!.input)),
    enabled: !!applied,
  });
  const volume = useQuery({
    queryKey: ['ma-vol', applied],
    queryFn: () => (applied!.mode === 'all' ? marketAbuseApi.getUnfilteredVolumeNotifications(applied!.input) : marketAbuseApi.getVolumeNotifications(applied!.input)),
    enabled: !!applied,
  });

  // Jump from MAR Instruments badge: prefill symbol + dates then fetch.
  const lastJump = useRef<string>('');
  useEffect(() => {
    if (!jump) return;
    const key = `${jump.symbol}~${jump.startDate}`;
    if (lastJump.current === key) return;
    lastJump.current = key;
    const start = jump.startDate ? String(jump.startDate).substring(0, 10) : y;
    setDraft((f) => ({ ...f, notifSymbol: jump.symbol, startDate: start, endDate: y }));
    setApplied({ mode: 'filtered', input: { startDate: start, endDate: y, notifSymbol: jump.symbol, notifExecutionVenue: null, skipCount: 0, maxResultCount: 1000 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump]);

  const fetchFiltered = () => {
    if (!draft.startDate || !draft.endDate) { toast.error('Date range is required.'); return; }
    if (!draft.notifSymbol) { toast.error('Instrument is required.'); return; }
    setApplied({ mode: 'filtered', input: base() });
  };
  const showAll = () => {
    setDraft((f) => ({ ...f, notifSymbol: null, notifExecutionVenue: null }));
    setApplied({ mode: 'all', input: { startDate: draft.startDate, endDate: draft.endDate, notifSymbol: null, notifExecutionVenue: null, skipCount: 0, maxResultCount: 1000 } });
  };

  const str = (v: string | number | null) => (v == null ? '' : String(v));
  const settleCols: DataGridColumn<SettlementTimeAlertDto>[] = [
    { key: 'ticket', title: 'Ticket', value: (r) => r.ticket, cell: (r) => num(r.ticket, 0) },
    { key: 'instrument', title: 'Instrument', value: (r) => r.instrument },
    { key: 'tradeTime', title: 'Time', value: (r) => r.tradeTime, cell: (r) => dmyhms(r.tradeTime) },
    { key: 'settlementTimePeriod', title: 'Settlement Time Period', value: (r) => r.settlementTimePeriod },
    { key: 'baseNotionalRc', title: 'Daily Traded Volume', className: 'text-end', value: (r) => r.baseNotionalRc, cell: (r) => num(r.baseNotionalRc, 0) },
    { key: 'login', title: 'Login', value: (r) => r.login, cell: (r) => <a href="#" className="fw-semibold" onClick={(e) => { e.preventDefault(); if (r.platformId) onOpenLogin(r.login, r.platformId); }}>{r.login}</a> },
    { key: 'uniqueId', title: 'Unique ID', value: (r) => r.uniqueId ?? '' },
    { key: 'connector', title: 'Connector', value: (r) => r.connector },
  ];
  const volCols: DataGridColumn<VolumeAlertDto>[] = [
    { key: 'instrument', title: 'Instrument', value: (r) => r.instrument },
    { key: 'baseNotionalRc', title: 'Daily Traded Volume', className: 'text-end', value: (r) => r.baseNotionalRc, cell: (r) => num(r.baseNotionalRc, 0) },
    { key: 'averageVolume', title: 'Average Daily Volume (M)', className: 'text-end', value: (r) => r.averageVolume, cell: (r) => num(r.averageVolume, 2) },
    { key: 'sensitivity', title: 'Percentage Sensitivity (%)', className: 'text-end', value: (r) => r.sensitivity, cell: (r) => num(r.sensitivity, 2) },
    { key: 'date', title: 'Date', value: (r) => r.date, cell: (r) => dmy(r.date) },
  ];

  return (
    <>
      <FilterBar
        card={false}
        fields={[
          {
            node: <div key="date"><label className="form-label">Date Range</label><DateRangePicker mode="range" maxDate={y} value={{ start: draft.startDate, end: draft.endDate }} onChange={(v) => setDraft((f) => ({ ...f, startDate: v.start, endDate: v.end }))} /></div>,
            pinned: true,
            width: 270,
          },
          <div key="symbol"><label className="form-label">Instrument</label><Select2Async placeholder="Select" value={str(draft.notifSymbol) || null} fetch={filterDataPaged.instruments} onChange={(v) => setDraft((f) => ({ ...f, notifSymbol: v || null }))} /></div>,
          <Select2 key="venue" label="Execution Venue" allowClear placeholder="All" value={str(draft.notifExecutionVenue)} options={venues.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, notifExecutionVenue: v ? Number(v) : null }))} />,
        ]}
        actions={
          <>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={showAll}><i className="fa fa-list me-1" />Show All</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={fetchFiltered} disabled={settlement.isFetching}>{settlement.isFetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch</button>
          </>
        }
      />
      <h6 className="fw-semibold mb-1 mt-3 border-top pt-2">Settlement Time Alerts</h6>
      <DataGrid columns={settleCols} data={settlement.data?.items ?? []} rowKey={(r) => `${r.ticket}-${r.login}`} pageSize={5} card={false} includeExcel excelFileName="Settlement Time Alerts" emptyMessage={applied ? 'No data.' : 'Fetch or Show All to load alerts.'} />
      <h6 className="fw-semibold mb-1 mt-4 border-top pt-2">Volume Alerts</h6>
      <DataGrid columns={volCols} data={volume.data?.items ?? []} rowKey={(r) => `${r.instrument}-${r.date}`} pageSize={5} card={false} includeExcel excelFileName="Volume Alerts" emptyMessage={applied ? 'No data.' : 'Fetch or Show All to load alerts.'} />
    </>
  );
}
